/**
 * Code 128 (Code Set B) encoding, for the sample label.
 *
 * ===========================================================================
 * WHY THIS IS WRITTEN OUT RATHER THAN INSTALLED
 * ===========================================================================
 *
 * A label is the one artefact of this system that leaves the building on a
 * physical sample, and what it carries is exactly the Sample ID and nothing
 * else (decision D68). That is a small, fixed, fully specified job: 107 module
 * patterns, a modulo-103 checksum, and a quiet zone. Taking a dependency to do
 * it would add a supply chain to the product for ninety lines of table.
 *
 * WHAT THIS IS NOT. It is not a general barcode library. Only Code Set B is
 * implemented, because the identifier is alphanumeric with hyphens and Set B
 * covers printable ASCII 32–126 in one pass. A character outside that range is
 * REFUSED rather than silently substituted: a label that quietly dropped a
 * character would scan as a different sample, which is worse than not printing.
 *
 * ===========================================================================
 * NOT YET VERIFIED AGAINST A PHYSICAL SCANNER.
 * ===========================================================================
 *
 * The encoder is checked against the structural invariants of the symbology in
 * `code128.test.ts` — every pattern is eleven modules, every pattern has even
 * bar parity, every pattern is unique, and the checksum arithmetic is exercised
 * — and that is genuinely a strong check on the table. It is NOT the same thing
 * as a scanner having read a printed label, which depends on the laboratory's
 * own printer resolution, label stock and quiet zone as much as on this code.
 * That verification belongs to the laboratory, and until it has happened this
 * must not be described as proven. See the limitation recorded in
 * `docs/PROJECT_DOCUMENTATION.md`.
 */

/**
 * The 107 module patterns of Code 128.
 *
 * Each entry is six element widths — bar, space, bar, space, bar, space —
 * except the stop pattern, which has seven. The widths are in modules, and each
 * character is eleven modules wide (the stop is thirteen).
 */
const PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213',
  '122312', '132212', '221213', '221312', '231212', '112232', '122132',
  '122231', '113222', '123122', '123221', '223211', '221132', '221231',
  '213212', '223112', '312131', '311222', '321122', '321221', '312212',
  '322112', '322211', '212123', '212321', '232121', '111323', '131123',
  '131321', '112313', '132113', '132311', '211313', '231113', '231311',
  '112133', '112331', '132131', '113123', '113321', '133121', '313121',
  '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111',
  '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114',
  '413111', '241112', '134111', '111242', '121142', '121241', '114212',
  '124112', '124211', '411212', '421112', '421211', '212141', '214121',
  '412121', '111143', '111341', '131141', '114113', '114311', '411113',
  '411311', '113141', '114131', '311141', '411131', '211412', '211214',
  '211232', '2331112',
];

/** Start character for Code Set B. */
const START_B = 104;
/** Stop pattern, the last entry in the table. */
const STOP = 106;

/** The narrowest and widest printable ASCII Code Set B covers. */
const FIRST_PRINTABLE = 32;
const LAST_PRINTABLE = 126;

export class Code128Error extends Error {
  constructor(readonly character: string) {
    super(`Code 128 Set B cannot encode ${JSON.stringify(character)}.`);
    this.name = 'Code128Error';
  }
}

/** True when every character of `value` can be carried by Code Set B. */
export function isEncodableAsCode128(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < FIRST_PRINTABLE || code > LAST_PRINTABLE) return false;
  }
  return true;
}

/**
 * One bar of the rendered symbol.
 *
 * Positions and widths are in MODULES, not pixels or millimetres. The caller
 * scales them to the physical width it has, which is what keeps this function
 * free of any assumption about label size or printer resolution.
 */
export interface Code128Bar {
  readonly x: number;
  readonly width: number;
}

export interface Code128Symbol {
  /** The bars to draw. Spaces are simply the gaps between them. */
  readonly bars: readonly Code128Bar[];
  /** Total width in modules, INCLUDING the quiet zone at both ends. */
  readonly totalModules: number;
  /** Modules of blank space required either side of the symbol. */
  readonly quietZoneModules: number;
  /** The checksum character's value, for the test to assert against. */
  readonly checksum: number;
}

/**
 * The quiet zone, in modules.
 *
 * The symbology requires at least ten. A symbol printed hard against the edge
 * of a label is a symbol a scanner cannot find the start of, which is one of
 * the commonest reasons a barcode that looks perfect will not read.
 */
const QUIET_ZONE_MODULES = 10;

/**
 * Encode a value as a Code 128 Set B symbol.
 *
 * Throws `Code128Error` for a character Set B cannot carry. A caller that
 * cannot tolerate an exception should ask `isEncodableAsCode128` first — and
 * should then print no barcode at all rather than a partial one.
 */
export function encodeCode128(value: string): Code128Symbol {
  if (value.length === 0) {
    throw new Code128Error('');
  }

  const values: number[] = [START_B];
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < FIRST_PRINTABLE || code > LAST_PRINTABLE) {
      throw new Code128Error(character);
    }
    values.push(code - FIRST_PRINTABLE);
  }

  // The checksum is the start value plus each data value times its
  // one-based position, modulo 103.
  let weighted = START_B;
  for (let index = 1; index < values.length; index += 1) {
    weighted += (values[index] as number) * index;
  }
  const checksum = weighted % 103;

  values.push(checksum, STOP);

  const bars: Code128Bar[] = [];
  let x = QUIET_ZONE_MODULES;
  for (const symbolValue of values) {
    const pattern = PATTERNS[symbolValue];
    if (pattern === undefined) {
      // Unreachable for values produced above; kept so a future edit to the
      // table cannot fail silently.
      throw new Code128Error(String(symbolValue));
    }
    for (let element = 0; element < pattern.length; element += 1) {
      const width = Number(pattern[element]);
      // Even indices are bars, odd are spaces.
      if (element % 2 === 0) bars.push({ x, width });
      x += width;
    }
  }

  return {
    bars,
    totalModules: x + QUIET_ZONE_MODULES,
    quietZoneModules: QUIET_ZONE_MODULES,
    checksum,
  };
}

/** Every pattern in the table, for the structural tests. */
export const CODE128_PATTERNS = PATTERNS;
